import { API_BASE, authHeaders } from './apiAuth'
import type { QueueOwner } from './miroQueue'
import type { SentMoodboard, WaitingMoodboard } from '../moodboard/send'
import type { ClipItem } from './projectClip'
import type { CanvasNode } from './canvasService'

/**
 * Mood boards on their way to a Miro board (workers/api/src/routes/miroLayouts.ts).
 * The website sends; the panel reads what is waiting, draws it, and lets it go.
 */
const ENDPOINT = `${API_BASE}/api/miro-layouts`

/**
 * One cut picture, left with the Worker until the panel places it
 * (workers/api/src/moodboardTemp.ts). Answers its key.
 */
export async function stageMoodboardPicture(picture: Blob): Promise<string> {
  const response = await fetch(`${API_BASE}/api/miro-moodboard/stage`, {
    method: 'POST',
    headers: { 'Content-Type': picture.type || 'image/jpeg', ...(await authHeaders('web')) },
    body: picture,
  })
  if (!response.ok) throw new Error(`Could not prepare a picture (HTTP ${response.status})`)
  return ((await response.json()) as { key: string }).key
}

export async function sendMoodboard(board: SentMoodboard): Promise<string> {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: JSON.stringify({ board }),
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(body?.error ?? `Could not send it (HTTP ${response.status})`)
  }
  return ((await response.json()) as { id: string }).id
}

/** Still waiting for a panel? False once it is on a board (or was dismissed). */
export async function moodboardWaiting(id: string): Promise<boolean | null> {
  try {
    const response = await fetch(`${ENDPOINT}?id=${encodeURIComponent(id)}`, {
      headers: { Accept: 'application/json', ...(await authHeaders('web')) },
    })
    if (response.status === 404) return false
    if (!response.ok) return null
    return true
  } catch {
    return null
  }
}

/** Never throws: an empty list when the answer could not be had. */
export async function readWaitingMoodboards(owner: QueueOwner): Promise<WaitingMoodboard[]> {
  try {
    const response = await fetch(ENDPOINT, { headers: { Accept: 'application/json', ...(await authHeaders(owner)) } })
    if (!response.ok) return []
    const body = (await response.json()) as { waiting?: WaitingMoodboard[] }
    return Array.isArray(body.waiting) ? body.waiting : []
  } catch {
    return []
  }
}

export async function letGoOfMoodboard(owner: QueueOwner, id: string): Promise<void> {
  await fetch(`${ENDPOINT}?id=${encodeURIComponent(id)}`, { method: 'DELETE', headers: await authHeaders(owner) })
}

/**
 * Frames from a Miro board on their way to a project (the panel's Copy to
 * project, 2026-10-13). The panel parks the piece; the website takes it.
 */
export interface MiroPiece {
  kind: 'to-project'
  title: string
  items: ClipItem[]
  nodes: CanvasNode[]
  files: string[]
}

export async function sendPieceToProject(piece: Omit<MiroPiece, 'kind'>): Promise<void> {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('panel')) },
    body: JSON.stringify({ board: { kind: 'to-project', ...piece } }),
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(body?.error ?? `Could not send it (HTTP ${response.status})`)
  }
}

/** The website: anything the panel sent for a project, taken (and let go of). Never throws. */
export async function takeMiroPieces(): Promise<MiroPiece[]> {
  try {
    const response = await fetch(`${ENDPOINT}?for=project`, {
      headers: { Accept: 'application/json', ...(await authHeaders('web')) },
    })
    if (!response.ok) return []
    const body = (await response.json()) as { waiting?: { id: string; board: MiroPiece }[] }
    const rows = Array.isArray(body.waiting) ? body.waiting : []
    const headers = await authHeaders('web')
    await Promise.all(
      rows.map((row) =>
        fetch(`${ENDPOINT}?id=${encodeURIComponent(row.id)}`, { method: 'DELETE', headers }).catch(() => undefined),
      ),
    )
    return rows.map((row) => row.board).filter((board) => board && board.kind === 'to-project')
  } catch {
    return []
  }
}
